// Frontend mirror of the canonical tenant lifecycle (Blueprint §3.1 / §4.6).
//
// Kept in lock-step with functions/src/lib/tenantLifecycle.ts. The operator
// console reads `firms.lifecycleState` and renders it through this single map so
// status badges never drift (deficiency D-32). Colours reference the existing
// theme tokens (theme.css) via Tailwind utility classes — no new colours.

export type TenantLifecycleState =
  | "lead"
  | "offer_draft"
  | "offer_sent"
  | "invite_accepted"
  | "auth_ready"
  | "setup_in_progress"
  | "setup_complete"
  | "demo_active"
  | "trial_active"
  | "proposal_sent"
  | "proposal_accepted"
  | "payment_pending"
  | "payment_received"
  | "active"
  | "payment_failed"
  | "suspended"
  | "cancelled"
  | "churned";

export type LifecycleGroup =
  | "lead"
  | "onboarding"
  | "ready_trial"
  | "active"
  | "payment_risk"
  | "suspended"
  | "closed";

export function lifecycleGroup(state: string): LifecycleGroup {
  switch (state) {
    case "lead":
    case "offer_draft":
    case "offer_sent":
      return "lead";
    case "invite_accepted":
    case "auth_ready":
    case "setup_in_progress":
      return "onboarding";
    case "setup_complete":
    case "demo_active":
    case "trial_active":
      return "ready_trial";
    case "proposal_sent":
    case "proposal_accepted":
    case "payment_received":
    case "active":
      return "active";
    case "payment_pending":
    case "payment_failed":
      return "payment_risk";
    case "suspended":
      return "suspended";
    case "cancelled":
    case "churned":
      return "closed";
    // Derived/legacy status + subscriptionStatus values (so the badge works when
    // fed firm.status or firm.subscriptionStatus, not only lifecycleState).
    case "trialing":
    case "demo":
      return "ready_trial";
    case "pending_invite":
      return "onboarding";
    case "inactive":
      return "closed";
    case "none":
    case "":
      return "lead";
    default:
      return "onboarding";
  }
}

// Canonical group → Tailwind token classes (mirrors theme.css colour tokens).
export const GROUP_BADGE_CLASSES: Record<LifecycleGroup, string> = {
  lead: "bg-slate-100 text-slate-600 border-slate-200",
  onboarding: "bg-blue-50 text-blue-700 border-blue-200",
  ready_trial: "bg-amber-50 text-amber-700 border-amber-200",
  active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  payment_risk: "bg-orange-50 text-orange-700 border-orange-200",
  suspended: "bg-red-50 text-red-700 border-red-200",
  closed: "bg-slate-50 text-slate-400 border-slate-200",
};

// Human label for chips/filters.
export const GROUP_LABELS: Record<LifecycleGroup, string> = {
  lead: "Lead",
  onboarding: "Onboarding",
  ready_trial: "Trial / Demo",
  active: "Active",
  payment_risk: "Payment risk",
  suspended: "Suspended",
  closed: "Closed",
};

export function lifecycleLabel(state: string): string {
  return state
    .split("_")
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
    .join(" ");
}
